import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CreationStatus,
  GetCreationOutput,
  GetMeasureDetailOutput,
  PatchCreationInput,
  PostingType,
  SnsType,
} from '@/api/creation';
import { ReactComponent as ChevronLeftIcon } from '@/assets/icons/bx-chevron-left.svg';
import { ReactComponent as ErrorCircleIcon } from '@/assets/icons/bx-error-circle.svg';
import { PhaseNavigation } from '@/components/ProjectManagements/PhaseNavigation/Components/PhaseNavigation';
import { deleteCreation, getCreation, getMeasure, patchCreation } from '@/hooks/Creation';
import { formatYYYYMMDDHHmm } from '@/utils/format';

import { phaseInfo } from '../interfaces/PRSTProductionManagementInterface';

import { ContentView } from './ContentView';
import DotsDropdown from './DotsDropdown';
import { ImageView } from './ImageView';
import { EditModal } from './modals/EditModal';

const getCreationTypeDescription = (type: PostingType): string => {
  switch (type) {
    case PostingType.Reel:
      return 'リール';
    case PostingType.Feed:
      return 'フィード';
    case PostingType.Stories:
      return 'ストーリーズ';
    case PostingType.XImagePost:
      return '画像あり投稿';
    case PostingType.XPost:
      return 'テキスト投稿';
    case PostingType.YoutubeShort:
      return 'YouTubeショート';
    case PostingType.Youtube:
      return 'YouTube通常';
    case PostingType.TikTok:
      return 'TikTok';
    case PostingType.OtherVideo:
      return 'その他動画';
    case PostingType.Other:
      return 'その他';
    default:
      return '';
  }
};

export const PostDetailView = () => {
  const getMeasureId = (): string => {
    const paths = window.location.pathname.split('/'); // ["/", "PRST_production_management", "(measureId)"]
    return paths.length > 2 && paths[1] === 'PRST_production_management' ? paths[2] : 'measureId';
  };
  const getCreationId = (): string => {
    const paths = window.location.pathname.split('/'); // ["/", "PRST_production_management", "(measureId)", "(creationId)"]
    return paths.length > 3 && paths[1] === 'PRST_production_management' ? paths[3] : 'creationId';
  };
  const urlParams = new URLSearchParams(window.location.search);
  const isContent = urlParams.get('isContent') ? true : false;
  const updatePostDetail = async () => {
    const response = await getCreation(getCreationId());
    setPostDetail(response);
    return response;
  };
  const [postDetail, setPostDetail] = useState<GetCreationOutput>({
    id: '',
    no: 1,
    postScheduledAt: new Date().toISOString(),
    type: PostingType.Other,
    status: CreationStatus.Draft,
  } as GetCreationOutput);
  const [projectDetail, setProjectDetail] = useState<GetMeasureDetailOutput>({
    snsAccountName: 'アカウント名',
    snsType: SnsType.Other,
    name: '施策名',
    status: 'in_progress',
    createdAt: new Date().toISOString(),
    postsPlan: 0,
    postsResult: 0,
    influencerSnsAccounts: {
      rows: [],
    },
  });
  const [openPostEditModal, setOpenPostEditModal] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleClick1 = () => {
    navigate(`/PRST_production_management/${getMeasureId()}`);
  };

  const statusToStepNum = (status: CreationStatus): number => {
    switch (status) {
      case CreationStatus.Draft:
        return 1;
      case CreationStatus.DraftReview:
        return 2;
      case CreationStatus.Contents:
        return 3;
      case CreationStatus.ContentsReview:
        return 4;
      case CreationStatus.Completed:
        return 5;
      default:
        return 1;
    }
  };
  const phase: phaseInfo[] = [
    {
      name: 'イメージ案制作',
      value: 'create',
      isCurrent: postDetail?.status === CreationStatus.Draft || postDetail?.status === null,
      isDone: 0 < statusToStepNum(postDetail?.status || CreationStatus.Draft),
    },
    {
      name: 'イメージ案承認',
      value: 'selection',
      isCurrent: postDetail?.status === CreationStatus.DraftReview,
      isDone: 1 < statusToStepNum(postDetail?.status || CreationStatus.Draft),
    },
    {
      name: 'コンテンツ制作',
      value: 'check',
      isCurrent: postDetail?.status === CreationStatus.Contents,
      isDone: 2 < statusToStepNum(postDetail?.status || CreationStatus.Draft),
    },
    {
      name: 'コンテンツ承認',
      value: 'doing',
      isCurrent: postDetail?.status === CreationStatus.ContentsReview,
      isDone: 3 < statusToStepNum(postDetail?.status || CreationStatus.Draft),
    },
    {
      name: '完了',
      value: 'done',
      isCurrent: false,
      isDone: 4 < statusToStepNum(postDetail?.status || CreationStatus.Draft),
    },
  ];

  useEffect(() => {
    getMeasure(getMeasureId()).then(async (response) => {
      if (response) {
        setProjectDetail(response);
      }
    });
    updatePostDetail();
  }, []);

  useEffect(() => {
    updatePostDetail();
  }, [postDetail.status]);

  return (
    <>
      <div className="relative w-full">
        <div className="mb-[30px] mt-[16px] flex min-h-[45px] max-w-[1181px] items-center justify-between border-b border-gray-200 py-[11px] pl-[16px] pr-[40px]">
          <div className="flex items-center">
            <div className="flex flex-wrap items-center">
              <button onClick={handleClick1}>
                <ChevronLeftIcon width={24} height={24} fill="#9CA3AF" />
              </button>
              <div className="ml-[30px] flex items-center justify-between gap-[15px]">
                <div className="flex items-center">
                  <p className="whitespace-nowrap align-middle text-xs font-normal leading-none text-[#374151]">
                    {projectDetail.name}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-base font-bold leading-6 text-gray-700">
                    No.{postDetail?.no < 1000 ? ('000' + String(postDetail.no)).slice(-4) : postDetail?.no}
                  </p>
                  <p className="whitespace-nowrap rounded bg-gray-100 px-[10px] py-[2px] text-xs font-medium leading-4 text-gray-800">
                    {getCreationTypeDescription(postDetail.type)}
                  </p>
                </div>
              </div>
              <p className="ml-[30px] whitespace-nowrap text-sm font-normal leading-5 text-gray-500">
                投稿予定日：{formatYYYYMMDDHHmm(postDetail.postScheduledAt)}
              </p>
            </div>
          </div>
          <div>
            <DotsDropdown
              modalText="この投稿制作"
              listType="measure"
              deleteFunction={() => {
                deleteCreation(getCreationId())
                  .catch((e) => {
                    console.log('deleteCreation error:', e.message);
                  })
                  .finally(() => {
                    navigate(`/PRST_production_management/${getMeasureId()}`);
                  });
              }}
              // InputのformatDateはモーダル内で選択した内容が反映される
              editFunction={() => {
                setOpenPostEditModal(true);
              }}
            />
          </div>
        </div>
        {/* <StepPanel_ImageApproving /> */}
        <div className="max-w-[1181px] px-6">{PhaseNavigation(phase)}</div>
        {postDetail?.status === CreationStatus.DraftReview && (
          <div className="mt-[25px] px-[24px]">
            <div className="flex h-[52px] w-full max-w-[1133px] items-center bg-blue-50 p-[16px]">
              <ErrorCircleIcon width={20} height={20} fill="#60A5FA" className="mr-[12px]" />
              <div className="flex h-[20px] w-[1069px] items-center">
                <span className="text-sm font-normal leading-5 text-blue-700">イメージ案を承認してください。</span>
              </div>
            </div>
          </div>
        )}
        {postDetail?.status === CreationStatus.ContentsReview && (
          <div className="mt-[25px] px-[24px]">
            <div className="flex h-[52px] w-full max-w-[1133px] items-center bg-blue-50 p-[16px]">
              <ErrorCircleIcon width={20} height={20} fill="#60A5FA" className="mr-[12px]" />
              <div className="flex h-[20px] w-[1069px] items-center">
                <span className="text-sm font-normal leading-5 text-blue-700">コンテンツを承認してください。</span>
              </div>
            </div>
          </div>
        )}
        {(postDetail?.contents || statusToStepNum(postDetail?.status ?? CreationStatus.Draft) > 2 || isContent) && (
          <ContentView postDetail={postDetail} updatePost={updatePostDetail} projectDetail={projectDetail} />
        )}
        {(postDetail?.draft || !isContent) && (
          <ImageView postDetail={postDetail} setPostDetail={setPostDetail} updatePost={updatePostDetail} />
        )}
        {/* 編集モーダル */}
        <EditModal
          title={'投稿概要編集'}
          open={openPostEditModal}
          initialDate={new Date(postDetail?.postScheduledAt)} // 初期日付を渡す
          onClose={() => setOpenPostEditModal(false)}
          onSave={(formattedDate: string) => {
            const patchCreationInput: PatchCreationInput = {
              type: postDetail.type,
              postingDate: formattedDate,
            };
            patchCreation(getCreationId(), patchCreationInput)
              .catch((e) => {
                console.log('patchCreation error:', e.message);
              })
              .finally(() => {
                setOpenPostEditModal(false);
                updatePostDetail();
              });
          }}
        />
      </div>
    </>
  );
};
